import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AddIcon from "@mui/icons-material/Add";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Button, Chip, Drawer, Grid, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CopilotButton from "../../components/CopilotButton/CopilotButton";
import { NurseDrawer } from "../../components/nurseDrawer";
import { type NurseChanges, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useLocalizedWeekDays } from "../../hooks/useDateLocalization";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { trackEvent } from "../../utils/analytics";
import { draftWeekPeriod, getDraftModeEnd, getDraftModeStart } from "../../utils/demoDraftWeeks";
import { formatNurseName } from "../../utils/formatters";
import { type Nurse, type NurseInShift, Preferences, type WeekData } from "../../utils/models";
import { getNurseChanges } from "../../utils/utilFunctions";
import { rdoMaxAmount, shiftTargetAmount, validateRDOAmount, validateTotalShiftAmount, validateWeekendsAmount, weekendsTargetAmount } from "./utils";
import { WarningViolation } from "./WarningViolation";

const drawerWidth = 450;

// Weeks shown on the hospital-demo nurse-detail schedule: week 0 ("Previous") + weeks 1-6,
// matching the header's date range. Keeps it from spanning the backend's full ~21-week window.
const HOSPITAL_NURSE_SCHEDULE_WEEKS = 7;

interface ShiftData {
	status: string;
	am?: { actual: number; needed: number };
	pm?: { actual: number; needed: number };
	startTime?: DateTime;
	endTime?: DateTime;
	avoidData?: AvoidData;
	removedFromShift?: boolean;
}

interface AvoidData {
	reason: string;
	type: string;
}

type ScheduleData = Record<string, ShiftData>;

export const NurseSchedule = () => {
	const { id, draftMode } = useParams();
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { hospitalData, getNurseWeekendShifts, getNurseRDOs, getNurseShifts, isHospitalDemo, isHospitalView, isSnfDemo } = useHospitalData();
	const [nurseChanges, setNurseChanges] = useState<NurseChanges>({
		additions: [],
		removals: [],
	});
	const navigate = useNavigate();
	const days = useLocalizedWeekDays().map((day) => day.toUpperCase());

	const draftModeSelected = useMemo(() => (draftMode ? draftMode === "true" && isHospitalDemo : false), [draftMode, isHospitalDemo]);
	const draftModePeriod = useMemo(
		() => (draftModeSelected ? { start: getDraftModeStart(), end: getDraftModeEnd() } : { start: undefined, end: undefined }),
		[draftModeSelected],
	);

	useEffect(() => {
		const changes = getNurseChanges(hospitalData, Number(id), undefined, draftModePeriod.start, draftModePeriod.end);
		setNurseChanges(changes);
	}, [id, hospitalData, draftModePeriod]);

	const toggleDrawer = () => {
		setIsDrawerOpen(!isDrawerOpen);
		trackEvent("nurse_schedule_drawer_toggled", {
			nurse_id: id,
			is_open: !isDrawerOpen,
		});
	};

	// Hospital demos: cap the nurse-detail schedule to the first 7 weeks so it matches the
	// header's date range (week 0 "Previous" + weeks 1-6). The backend sends ~21 weeks of
	// history+future, which read as "going back months". Other demos are unaffected.
	const isHospitalDemoView = isHospitalView || isHospitalDemo;
	const sliceWeeksByMode = useCallback(
		(weeks: WeekData[], filterByDraftMode: boolean): WeekData[] => {
			if (filterByDraftMode) return weeks.slice(weeks.length - (draftWeekPeriod + 1), weeks.length);
			return isHospitalDemoView ? weeks.slice(0, HOSPITAL_NURSE_SCHEDULE_WEEKS) : weeks;
		},
		[isHospitalDemoView],
	);

	const generateScheduleData = useCallback(
		(weeks: WeekData[], nurseId: string, nurses: Nurse[], filterByDraftMode: boolean): ScheduleData => {
			const data: ScheduleData = {};
			const avoidDates = new Map<string, AvoidData>();

			// Process avoid dates
			const nurseData: Nurse | undefined = nurses.find((n: Nurse) => n.id === Number.parseInt(nurseId));
			if (nurseData) {
				nurseData.employeeSpecificPreferences
					.filter((pref) => pref.type === Preferences.Avoid)
					.forEach((specificPreference) => {
						const dateString = specificPreference.start_date.toFormat("yyyy-MM-dd");
						avoidDates.set(dateString, {
							reason: specificPreference.data || "",
							type: specificPreference.type,
						});
					});
			}

			sliceWeeksByMode(weeks, filterByDraftMode).forEach((week) => {
				week.weeklyShifts.forEach((shift) => {
					shift.days.forEach((day) => {
						const dateString = day.date.toFormat("yyyy-MM-dd");
						const nurseInShift = day.nurses.find((n: NurseInShift) => n.nurseId === Number.parseInt(nurseId));
						const isNurseWorking = nurseInShift !== undefined;
						const isNightShift = shift.type === "Night";

						// Check if this date has an avoid status
						const avoidStatus = avoidDates.get(dateString);

						// Check if nurse was removed from shift
						const isNurseRemoved = nurseInShift?.isPendingToDelete;

						// Calculate actual and needed staff for the shift
						const actual = day.nurses.filter((n: NurseInShift) => !n.isPendingToDelete).length;
						const needed = day.staffNo;

						// If we already have an entry for this date, we need to update it
						if (data[dateString]) {
							const shiftInfo = { actual, needed };

							// Update shift data and status based on shift type
							if (isNightShift) {
								data[dateString].pm = shiftInfo;
								data[dateString].status = isNurseWorking ? "pm_working" : data[dateString].status;
							} else {
								data[dateString].am = shiftInfo;
								data[dateString].status = isNurseWorking ? "am_working" : data[dateString].status;
							}
							if (!data[dateString].startTime) {
								data[dateString].startTime = nurseInShift?.startTime;
							}
							if (!data[dateString].endTime) {
								data[dateString].endTime = nurseInShift?.endTime;
							}
						} else {
							// Create new entry for this date
							data[dateString] = {
								status: isNurseWorking ? (isNightShift ? "pm_working" : "am_working") : "not_working",
								avoidData: avoidStatus ? avoidStatus : undefined,
								removedFromShift: isNurseRemoved,
								startTime: nurseInShift?.startTime,
								endTime: nurseInShift?.endTime,
							};
							if (isNightShift) {
								data[dateString].pm = { actual, needed };
							} else {
								data[dateString].am = { actual, needed };
							}
						}
					});
				});
			});
			return data;
		},
		[sliceWeeksByMode],
	);

	// Generate mock data for the schedule
	const scheduleData = useMemo(() => {
		if (!hospitalData.weeks || !hospitalData.nurses || !id) return {};
		return generateScheduleData(hospitalData.weeks, id, hospitalData.nurses, draftModeSelected);
	}, [hospitalData.weeks, hospitalData.nurses, id, draftMode, generateScheduleData]);

	const formatDate = (date: DateTime, isFirstDayOfSchedule: boolean) => {
		const day = date.day;
		const month = date.toFormat("MMM");
		const isFirstOfMonth = day === 1;

		if (isFirstDayOfSchedule || isFirstOfMonth) {
			return `${month} ${day}`;
		}
		return day.toString();
	};

	const getShiftChip = (shiftData: ShiftData | undefined, weekIndex: number, dayIndex: number) => {
		const removed = nurseChanges.removals.find((s) => s.week === weekIndex && s.day === dayIndex);
		const added = nurseChanges.additions.find((s) => s.week === weekIndex && s.day === dayIndex);
		if (!shiftData) {
			return (
				<Box sx={{ display: "flex", gap: 3 }}>
					<Box
						sx={{
							width: 24,
							height: 24,
							borderRadius: "50%",
							bgcolor: "#f3f4f6",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
					</Box>
					<Box
						sx={{
							width: 24,
							height: 24,
							borderRadius: "50%",
							bgcolor: "#f3f4f6",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
					</Box>
				</Box>
			);
		}

		if (shiftData.status === "am_working") {
			return drawAccurateShiftHours({
				mode: "day",
				removed: removed !== undefined,
				shiftData: shiftData,
			});
		}
		if (shiftData.status === "pm_working") {
			return drawAccurateShiftHours({
				mode: "night",
				removed: removed !== undefined,
				shiftData: shiftData,
			});
		}
		if (shiftData.status === "not_working") {
			if (!shiftData.avoidData) {
				return (
					<Box sx={{ display: "flex", gap: 3 }}>
						<Box
							sx={{
								width: 24,
								height: 24,
								borderRadius: "50%",
								bgcolor: "#f3f4f6",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
						</Box>
						<Box
							sx={{
								width: 24,
								height: 24,
								borderRadius: "50%",
								bgcolor: "#f3f4f6",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
						</Box>
					</Box>
				);
			}
			return <Chip label={shiftData.avoidData.reason} size="small" color="warning" sx={{ mt: 1 }} />;
		}
		return (
			<Box sx={{ display: "flex", gap: 3 }}>
				<Box
					sx={{
						width: 24,
						height: 24,
						borderRadius: "50%",
						bgcolor: "#f3f4f6",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
				</Box>
				<Box
					sx={{
						width: 24,
						height: 24,
						borderRadius: "50%",
						bgcolor: "#f3f4f6",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<AddIcon sx={{ color: "#4b5563", fontSize: 16 }} />
				</Box>
			</Box>
		);
	};

	const startDate = useMemo(() => {
		const dateStr = hospitalData.weeks?.[0]?.weeklyShifts?.[0]?.days?.[0]?.date;

		if (!dateStr) {
			console.warn("No valid date string found, using current date");
			return DateTime.now();
		}

		return dateStr;
	}, [hospitalData.weeks]);

	const endDate = useMemo(() => {
		return startDate.plus({ days: 6 * 7 + 6 });
	}, [startDate]);

	const dateRangeText = useMemo(() => {
		return `${startDate.toFormat("MMM d")} - ${endDate.toFormat("MMM d")}`;
	}, [startDate, endDate]);

	// Add this near the top of the component where other calculations are done
	const nurse = useMemo(() => {
		if (!hospitalData.nurses || !id) return null;
		return hospitalData.nurses.find((n) => n.id === Number.parseInt(id));
	}, [hospitalData.nurses, id]);

	const totalShifts = useMemo(() => {
		if (!nurse) return 0;

		return getNurseShifts(nurse.id, draftModePeriod.start, draftModePeriod.end);
	}, [nurse, getNurseShifts, draftModePeriod]);

	const nurseChangesAmount = useMemo(() => {
		return Math.max(nurseChanges.additions.length, nurseChanges.removals.length);
	}, [nurseChanges]);

	const weekendShifts = useMemo(() => {
		if (!nurse) return 0;

		return getNurseWeekendShifts(nurse.id, draftModePeriod.start, draftModePeriod.end);
	}, [nurse, getNurseWeekendShifts, draftModePeriod]);

	const rdoCount = useMemo(() => {
		if (!nurse) return 0;

		return getNurseRDOs(nurse.id, draftModePeriod.start, draftModePeriod.end);
	}, [nurse, draftModePeriod, getNurseRDOs]);

	const handleApply = () => {
		trackEvent("nurse_schedule_apply_clicked", {
			nurse_id: id,
			changes_count: nurseChangesAmount,
		});
		// Apply logic here
	};

	const handleCancel = () => {
		trackEvent("nurse_schedule_cancel_clicked", {
			nurse_id: id,
		});
		navigate(-1);
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 60px)",
				width: "100%",
				position: "absolute",
				left: "0",
				top: "60px",
				overflow: "hidden",
			}}
		>
			<Box
				component="main"
				sx={{
					height: "100%",
					width: isDrawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
					transition: "margin width 0.2s",
					marginRight: isDrawerOpen ? `${drawerWidth}px` : 0,
					overflow: "auto",
					position: "relative",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 3,
						flexWrap: "wrap",
						background: "rgba(216, 208, 232, 1)",
						height: "75px",
						px: 2,
						py: 1,
						position: "sticky",
						top: 0,
						zIndex: 1,
						width: "100%",
					}}
				>
					<Box>
						<Typography
							sx={{
								color: "text.secondary",
								fontSize: 12,
							}}
						>
							{dateRangeText}
						</Typography>
						<Typography variant="h6">{nurse ? formatNurseName(nurse, lastNameFirst) : t("unknown.nurse")}</Typography>
					</Box>

					{/* Stats */}
					<Box sx={{ display: "flex", gap: 3 }}>
						<Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1 }}>
							<Typography
								sx={{
									color: "text.secondary",
									fontSize: 12,
								}}
							>
								{t("type")}
							</Typography>
							<Typography>{nurse?.role || t("unknown.type")}</Typography>
						</Box>
						<Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1 }}>
							<WarningViolation
								displayViolation={!validateTotalShiftAmount(totalShifts) && (isSnfDemo || isHospitalDemo)}
								violation={`Total shifts must be at least ${shiftTargetAmount}.`}
							>
								<Typography
									sx={{
										color: "text.secondary",
										fontSize: 12,
									}}
								>
									{t("total.shifts")}
								</Typography>
							</WarningViolation>
							<Typography>{totalShifts}</Typography>
						</Box>
						<Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1 }}>
							<WarningViolation
								displayViolation={!validateWeekendsAmount(weekendShifts) && (isSnfDemo || isHospitalDemo)}
								violation={`Weekend shifts must be at least ${weekendsTargetAmount}.`}
							>
								<Typography
									sx={{
										color: "text.secondary",
										fontSize: 12,
									}}
								>
									{t("weekends")}
								</Typography>
							</WarningViolation>
							<Typography>{weekendShifts}</Typography>
						</Box>
						<Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1 }}>
							<WarningViolation
								displayViolation={!validateRDOAmount(rdoCount) && (isSnfDemo || isHospitalDemo)}
								violation={`RDOs must not exceed ${rdoMaxAmount}.`}
							>
								<Typography
									sx={{
										color: "text.secondary",
										fontSize: 12,
									}}
								>
									{t("rdos")}
								</Typography>
							</WarningViolation>
							<Typography>{rdoCount}</Typography>
						</Box>
						<Box sx={{ borderLeft: "1px solid #e0e0e0", pl: 1 }}>
							<Typography
								sx={{
									color: "text.secondary",
									fontSize: 12,
								}}
							>
								{t("changes")}
							</Typography>
							<Typography>{nurseChangesAmount}</Typography>
						</Box>
					</Box>

					{/* Buttons */}
					<Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
						<Button variant="text" color="inherit" onClick={handleCancel}>
							{t("cancel")}
						</Button>
						<Button
							sx={{
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
								color: "primary.dark",
								"&:hover": { bgcolor: "#DDD6FE" },
							}}
							onClick={handleApply}
						>
							{t("apply")}
						</Button>
					</Box>
				</Box>
				<Box sx={{ p: 3, minWidth: "fit-content" }}>
					<Grid
						container
						sx={{
							flexDirection: "row",
							justifyContent: "flex-start",
							height: "100%",
						}}
					>
						{/* Week numbers column */}
						<Grid container sx={{ flexDirection: "column", flexBasis: "3.33%" }}>
							<Grid sx={{ height: "40px" }} />
							{sliceWeeksByMode(hospitalData.weeks, draftModeSelected).map((week, index) => (
								<Grid container key={index} sx={{ flexGrow: 1, py: 1 }}>
									<Paper
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: "0px",
											bgcolor: "#e0e7ff",
											background: "rgba(216, 208, 232, 1)",
											borderRight: "1px solid #e0e0e0",
											width: "100%",
										}}
									>
										<Typography
											sx={{
												transform: "rotate(270deg)",
												opacity: index === 0 ? 0.4 : 1,
												whiteSpace: "nowrap",
											}}
											variant="body2"
										>
											{index === 0 ? "Previous" : `Week ${index}`}
										</Typography>
									</Paper>
								</Grid>
							))}
						</Grid>

						{/* Days and schedule grid */}
						<Grid container sx={{ flexDirection: "column", height: "100%", flexBasis: "96%" }}>
							{/* Header row */}
							<Grid container sx={{ height: "40px" }}>
								{days.map((day, index) => (
									<Grid key={index} sx={{ flex: 1 }}>
										<Paper
											elevation={0}
											sx={{
												px: 1,
												py: 0,
												height: "100%",
												borderBottom: "1px solid #e0e0e0",
												display: "flex",
												alignItems: "center",
												borderRadius: "0px",
												justifyContent: "center",
											}}
										>
											<WbSunnyIcon sx={{ fontSize: 16, color: "#fbbf24" }} />
											<Typography variant="body2" sx={{ fontWeight: "bold", px: 2 }}>
												{day}
											</Typography>
											<NightsStayIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
										</Paper>
									</Grid>
								))}
							</Grid>

							{/* Schedule rows */}
							{sliceWeeksByMode(hospitalData.weeks, draftModeSelected).map((_, weekIndex) => (
								<Grid container key={weekIndex} sx={{ flexGrow: 1 }}>
									{days.map((_, dayIndex) => {
										const cellDate = sliceWeeksByMode(hospitalData.weeks, draftModeSelected)[weekIndex].weeklyShifts[0].days[dayIndex].date;
										const isFirstDayOfSchedule = weekIndex === 0 && dayIndex === 0;
										const formattedDate = formatDate(cellDate, isFirstDayOfSchedule);
										const dateString = (cellDate.toISO() as string).split("T")[0];
										const shiftData = scheduleData[dateString];
										return (
											<Grid key={dayIndex} sx={{ flex: 1 }}>
												<Paper
													sx={{
														height: "100%",
														border: "1px solid #e0e0e0",
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														justifyContent: "flex-start",
														borderRadius: "0px",
														p: 1,
														position: "relative",
														...(weekIndex === 0 && {
															opacity: 0.4,
															backgroundColor: "rgba(255, 255, 255, 0.7)",
														}),
													}}
													onClick={() => {
														trackEvent("nurse_schedule_cell_clicked", {
															nurse_id: id,
															date: dateString,
															week_index: weekIndex,
															day_index: dayIndex,
														});
													}}
												>
													<Typography
														variant="body2"
														sx={{
															mt: 1,
															fontSize: formattedDate.length > 2 ? "0.75rem" : "0.875rem",
															fontWeight: "medium",
															lineHeight: 1,
														}}
													>
														{formattedDate}
													</Typography>
													<Box sx={{ width: "100%", mt: 1 }}>
														<Box
															sx={{
																gap: 2,
																display: "flex",
																justifyContent: "center",
																alignItems: "center",
																mb: 0.5,
															}}
														>
															<Chip
																label={`${shiftData?.am?.actual || 0}/${shiftData?.am?.needed || 0}`}
																size="small"
																variant="filled"
																sx={{
																	height: "auto",
																	"& .MuiChip-label": {
																		fontSize: "0.75rem",
																		py: 0.25,
																		px: 1,
																	},
																	mr: 1,
																}}
															/>
															<Chip
																label={`${shiftData?.pm?.actual || 0}/${shiftData?.pm?.needed || 0}`}
																size="small"
																variant="filled"
																sx={{
																	height: "auto",
																	"& .MuiChip-label": {
																		fontSize: "0.75rem",
																		py: 0.25,
																		px: 1,
																	},
																}}
															/>
														</Box>
													</Box>
													<Box
														sx={{
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															gap: 1,
															mt: "auto",
															width: "100%",
														}}
													>
														{getShiftChip(shiftData, weekIndex, dayIndex)}
													</Box>
												</Paper>
											</Grid>
										);
									})}
								</Grid>
							))}
						</Grid>
					</Grid>
				</Box>
			</Box>
			<Drawer
				anchor="right"
				open={isDrawerOpen}
				onClose={toggleDrawer}
				variant="persistent"
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					"& .MuiDrawer-paper": {
						width: drawerWidth,
						boxSizing: "border-box",
						position: "absolute",
						height: "100%",
						zIndex: 1,
					},
				}}
			>
				<NurseDrawer onClose={toggleDrawer} />
			</Drawer>
			<CopilotButton
				sx={{
					position: "fixed",
					zIndex: 2,
					bottom: 16,
					right: 16,
					height: 56,
					width: 56,
				}}
				open={isDrawerOpen}
				onClick={() => {
					toggleDrawer();
					trackEvent("nurse_schedule_copilot_button_clicked", {
						nurse_id: id,
						current_state: isDrawerOpen ? "open" : "closed",
					});
				}}
			/>
		</Box>
	);
};

function drawAccurateShiftHours({ shiftData, removed, mode }: { shiftData: ShiftData; removed: boolean; mode: "day" | "night" }) {
	return (
		<Chip
			icon={<AccessTimeFilledIcon />}
			label={
				<span style={{ textDecoration: removed ? "line-through" : "none" }}>
					{shiftData.startTime?.toFormat("h:mm a")} - {shiftData.endTime?.toFormat("h:mm a")}
				</span>
			}
			size="small"
			sx={{
				bgcolor: mode === "day" ? "#fef3c7" : mode === "night" ? "#dbeafe" : "#e9bef7",
				color: mode === "day" ? "#92400e" : mode === "night" ? "#1e40af" : "#6f0e92",
				"& .MuiChip-icon": {
					color: mode === "day" ? "#92400e" : mode === "night" ? "#1e40af" : "#6f0e92",
				},
			}}
		/>
	);
}
